import { isFunction } from 'lodash-es';
import type { Node } from '@xyflow/react';
import { InvalidTypeError } from '@shared/utils/build/flow/error';

import { OutputCanvasData } from '@shared/typings/flow/classes/OutputCanvas';
import { OutputTextData } from '@shared/typings/flow/classes/OutputText';
import { OutputImageData } from '@shared/typings/flow/classes/OutputImage';
import { InputImageData } from '@shared/typings/flow/classes/InputImage';
import { InputTextData } from '@shared/typings/flow/classes/InputText';
import {
	ENodeInputTypeCode,
	ENodeOutputTypeCode,
	INodeData,
} from '@shared/typings/flow/common/data';
import { FlowNode } from '@shared/typings/flow/common/node';
import { OutputVideoData } from '@shared/typings/flow/classes/OutputVideo';
import { OutputLongVideoData } from '@shared/typings/flow/classes/OutputLongVideo';

const FlowDataCreator = {
	[ENodeInputTypeCode.Image]: () => new InputImageData(),
	[ENodeInputTypeCode.Text]: () => new InputTextData(),
	[ENodeOutputTypeCode.Image]: () => new OutputImageData(),
	[ENodeOutputTypeCode.Text]: () => new OutputTextData(),
	[ENodeOutputTypeCode.Canvas]: () => new OutputCanvasData(),
	[ENodeOutputTypeCode.Video]: () => new OutputVideoData(),
	[ENodeOutputTypeCode.LongVideo]: () => new OutputLongVideoData(),
};

type TFlowDataCreator = typeof FlowDataCreator;
type TFlowDataCreatorKeys = keyof TFlowDataCreator;

const createEmptyData = <T extends TFlowDataCreatorKeys>(
	type: T,
): ReturnType<TFlowDataCreator[T]> => {
	if (!isFunction(FlowDataCreator[type])) {
		throw InvalidTypeError(type);
	}

	return FlowDataCreator[type]() as never;
};

type TEmptyReturnType<T extends TFlowDataCreatorKeys> = ReturnType<typeof createEmptyData<T>>;

/**
 * 创建空的 node.data 类型
 * @param type
 * @param mixin
 * @returns
 */
export const createFlowData = <T extends TFlowDataCreatorKeys>(
	type: T,
	mixin?: Partial<Omit<INodeData.UnionData, 'type'>>,
): TEmptyReturnType<T> => {
	return Object.assign(createEmptyData(type), mixin ?? {}) as never;
};

const createEmptyFlowNode = (data: INodeData.UnionData, mixin: Partial<Node>): Node => {
	const { id, type } = data;

	return {
		id,
		position: { x: 0, y: 0 },
		data,
		...mixin,
		type,
	};
};

/**
 * 创建空的画板节点
 * @param type
 * @param dataMixin
 * @param nodeMixin
 * @returns
 */
export const createFlowNode = <T extends TFlowDataCreatorKeys>(
	type: T,
	dataMixin: Partial<INodeData.UnionData> = {},
	nodeMixin: Partial<Node> = {},
): FlowNode => {
	const nodeData = createFlowData(type, dataMixin);
	const node = createEmptyFlowNode(nodeData, nodeMixin) as FlowNode;

	return node;
};
